import {
    Button,
    CardMedia,
    CircularProgress,
    Tooltip,
    Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import Title from "../../components/Title";
import axios from "../../MyAxios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MentionsInput, Mention } from "react-mentions";
import mentionStyle from "./mentionStyles";
import mentionsInputStyle from "./mentionsInputStyles";
import {
    ROOT,
    UPLOAD_FILE,
    GET_GROUP,
    CHECK_GROUP_DISABLED_FOR_EVENTS,
    CREATE_CAROUSEL_POST,
} from "../../constants/api";
import {
    addAllToConnections,
    addAllToGroups,
    getLinkMetadata,
    resetPost,
    setTags,
    setMedias,
    setPrimaryCarouselItemIndex,
    addToPostCommunities,
    addAllToCommunities,
} from "../../redux/postSlice";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Layout from "../../Layout";
import upload from "../../assets/img/upload.svg";
import useLoggedIn from "../../hooks/useLoggedIn";
import { addToPosts, updatePost } from "@redux/prevPostsSlice";
import MyAvatar from "@components/admin/MyAvatar";
import EmojiSelector from "@components/emoji-selector/EmojiSelector";
import AddComCon from "./AddComCon";
import Loader from "@components/Loader";
import OpenGraphInfo from "@components/OpenGraphInfo";
import { Close as CloseIcon, PlayArrow } from "@mui/icons-material";
import validator from "validator";
import PostTypeTabs from "../../components/PostTypeTabs.js";


const useStyles = makeStyles((theme) => ({
    border: { borderWidth: 2, borderColor: "#887E7E" },
    borderGreen: { borderWidth: 1, borderColor: "#51CDA0" },
    textColor: { color: theme.palette.primary.main },
    button: { backgroundColor: theme.palette.primary.main, color: "#fff" },
    fontSmall: { fontSize: "10px" },
    bottomImg: { position: "fixed", bottom: 0, right: 0, left: 0 },
    colorGreen: { backgroundColor: "#e5f8f1" },
    bgGrey: { backgroundColor: "#efefef" },
    bgGreen: { backgroundColor: "#e5f8f1" },
    textGrey: { color: "#626262" },
}));

const SingleMediaUpload = ({ media, onRemoveMedia, updateMedia, className = "" }) => {
    return (
        <div className={className} style={{ position: "relative" }}>
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    onRemoveMedia(media);
                }}
                className="absolute cursor-pointer top-[-5px] right-[-5px] z-10">
                <div className="bg-red-300 rounded-full flex justify-center items-center w-5 h-5">
                    <Typography color={"red"}>
                        X
                    </Typography>
                </div>
            </div>

            <div
                className={clsx(
                    "flex flex-col items-center justify-center z-0",
                    "w-full h-full"
                )}
            >
                <div>
                    {media.type === "video" ?
                        <div className="relative h-full w-full">
                            <CardMedia
                                crossOrigin="anonymous"
                                component={"video"}
                                // controls
                                className={"h-full w-full object-cover"}
                                image={media.url}
                                src={media.url}
                                alt="Stream Media"
                                muted
                                playsInline
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black/50">
                                    <PlayArrow
                                        sx={{
                                            fontSize: 42,
                                            color: "white",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        : media.type === "image" ? <img
                            src={media.url}
                            alt="Upload"
                            className={"h-full w-full object-cover"}
                        />
                            : media.type === "opengraph" ?
                                (media.linkPreviewLoading
                                    ? <Loader />
                                    : media.hideLinkPreview ?
                                        <div className="flex justify-end">
                                            <Button
                                                variant="contained"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateMedia({ hideLinkPreview: false });
                                                }}
                                            >
                                                Show link preview
                                            </Button>
                                        </div>
                                        : <div className="relative">
                                            <OpenGraphInfo
                                                className="h-full w-full"
                                                imageOnly
                                                data={{ ...media.metadata, ogUrl: null, requestUrl: null, sourceLink: null }}  // disallow <a href>
                                            />
                                            <Tooltip title="Remove link preview">
                                                <CloseIcon
                                                    className="cursor-pointer absolute top-5 right-[-5px]"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateMedia({ hideLinkPreview: true });
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>)
                                : null}
                </div>
            </div>
        </div>
    )
}

const DescriptionInput = ({ errors, media, updateMedia, onOpenGraphDetected }) => {
    const dispatch = useDispatch();

    const [descCursorPos, setDescCursorPos] = useState(0);
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [mentionSuggestionsLoading, setMentionSuggestionsLoading] = useState(false);

    const setPostLinkPreview = (value) => {
        updateMedia({ linkPreview: value });
    };
    const setPostLinkPreviewError = (value) => {
        updateMedia({ linkPreviewError: value });
    };
    const setPostLinkPreviewLoading = (value) => {
        updateMedia({ previewLoading: value });
    };

    // firstLinkRef: locks the first URL typed — only it ever gets OG treatment
    // lastFetchedLinkRef: dedup guard so we don't re-fetch the same URL twice
    const lastFetchedLinkRef = useRef(null);
    const firstLinkRef = useRef(null);

    const handleSelectDescriptionEmoji = (emoji) => {
        const descArray = media.description.split("");
        descArray.splice(descCursorPos, 0, emoji);
        const newDesc = descArray.join("");
        updateMedia({ description: newDesc });
    };

    const decodeHtmlEntities = (str) => {
        if (!str || typeof str !== "string") return str;
        try {
            const txt = document.createElement("textarea");
            txt.innerHTML = str;
            return txt.value;
        } catch (e) {
            return str;
        }
    };

    const decodeMetadata = (metadata) => {
        if (!metadata || typeof metadata !== "object") return metadata;
        const decoded = {};
        for (const [key, val] of Object.entries(metadata)) {
            decoded[key] = typeof val === "string" ? decodeHtmlEntities(val) : val;
        }
        return decoded;
    };

    const extractUrl = (text) => {
        if (!text) return null;
        let t = String(text).trim();
        try {
            t = decodeURIComponent(t);
        } catch (e) { }
        t = t.replace(/^<|>$/g, "");

        const httpRegex = /https?:\/\/[^\s<>'")]+/i;
        const wwwRegex = /www\.[^\s<>'")]+/i;
        const domainRegex =
            /(?:https?:\/\/)?(?:www\.)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s<>'"]*)?/i;

        let match = t.match(httpRegex) || t.match(wwwRegex) || t.match(domainRegex);
        if (!match) return null;

        let link = match[0].replace(/[.,;:!?')\]]+$/g, "");
        if (!/^https?:\/\//i.test(link)) link = "https://" + link;

        try {
            return new URL(link).toString();
        } catch (e) {
            return null;
        }
    };

    const tryFetchOG = (currentValue) => {
        if (media.hideLinkPreview) return;

        const extracted = extractUrl(currentValue || "");
        if (!extracted) {
            setPostLinkPreviewError("");
            return;
        }

        // URL changed from the locked one — reset locks to allow new fetch
        if (firstLinkRef.current && extracted !== firstLinkRef.current) {
            firstLinkRef.current = null;
            lastFetchedLinkRef.current = null;
            setPostLinkPreview(undefined);
            setPostLinkPreviewError("");
        }

        // Already fetched
        if (extracted === lastFetchedLinkRef.current) return;

        if (!validator.isURL(extracted)) {
            setPostLinkPreviewError("Invalid URL in description.");
            return;
        }

        setPostLinkPreviewError("");

        firstLinkRef.current = extracted;
        lastFetchedLinkRef.current = extracted;
        setPostLinkPreviewLoading(true);

        dispatch(
            getLinkMetadata({
                link: extracted,
                onSuccess: (metadata) => {
                    const meta = decodeMetadata(metadata);
                    if (!meta?.ogDescription && (!meta?.ogImage || meta.ogImage.length === 0)) {
                        setPostLinkPreviewError("Please double-check the url and try again.");
                        return;
                    }

                    setPostLinkPreview({ sourceLink: extracted, ...meta });
                    onOpenGraphDetected(meta, extracted);

                    if (meta?.ogDescription) {
                        const currentDesc = (currentValue || "").trim();

                        // Keep the URL (and any other text) as-is, append OG description after
                        const newDesc = currentDesc
                            ? `${currentDesc}\n\n${meta.ogDescription}`
                            : meta.ogDescription;

                        updateMedia({ description: newDesc });
                    }
                },
                onError: (error) => setPostLinkPreviewError(error),
            }),
        ).finally(() => setPostLinkPreviewLoading(false));
    };

    const handleEnterPostLink = useCallback(() => {
        if (!media.link) {
            setPostLinkPreview(undefined);
            setPostLinkPreviewError("");
            firstLinkRef.current = null;
            lastFetchedLinkRef.current = null;
            return;
        }

        let payloadLink = media.link?.trim();
        payloadLink = extractUrl(payloadLink);

        if (!payloadLink) {
            setPostLinkPreviewError("Invalid URL in post link.");
            return;
        }

        if (media.hideLinkPreview) return;

        setPostLinkPreviewError("");

        setPostLinkPreview();
        updateMedia({ link: payloadLink });

        firstLinkRef.current = payloadLink;
        lastFetchedLinkRef.current = payloadLink;

        setPostLinkPreviewLoading(true);
        dispatch(
            getLinkMetadata({
                link: payloadLink,
                onSuccess: (metadata) => {
                    if (!metadata?.ogDescription && (!metadata?.ogImage || metadata.ogImage.length === 0)) {
                        setPostLinkPreviewError("Please double-check the url and try again.");
                        return;
                    }
                    setPostLinkPreview({ sourceLink: payloadLink, ...metadata });
                    if (metadata?.ogDescription) {
                        updateMedia({ description: metadata?.ogDescription });
                    }
                },
                onError: (error) => setPostLinkPreviewError(error),
            }),
        ).then(() => setPostLinkPreviewLoading(false));
    }, []);

    useEffect(() => {
        if (!media.description) {
            firstLinkRef.current = null;
            lastFetchedLinkRef.current = null;
            setPostLinkPreview(undefined);
            setPostLinkPreviewError("");
            return;
        }

        const currentUrl = extractUrl(media.description);
        if (
            firstLinkRef.current &&
            currentUrl &&
            currentUrl !== firstLinkRef.current
        ) {
            firstLinkRef.current = null;
            lastFetchedLinkRef.current = null;
            setPostLinkPreview(undefined);
            setPostLinkPreviewError("");
        }

        let mentionStrings = media.description.match(/\B@[a-z0-9_-]+/gi);
        setMentionSuggestions([]);
        if (mentionStrings?.length) {
            mentionStrings = mentionStrings.map((str) => str.slice(1));
            setMentionSuggestionsLoading(true);
            axios
                .get("/user/get-user-by-usernames", {
                    params: { usernames: mentionStrings },
                })
                .then((response) => setMentionSuggestions(response.data))
                .finally(() => setMentionSuggestionsLoading(false));
        }
    }, [media.description, handleEnterPostLink]);

    useEffect(() => {
        if (!media.hideLinkPreview && media.link) handleEnterPostLink();
    }, [media.hideLinkPreview, media.link, handleEnterPostLink]);

    useEffect(() => {
        setPostLinkPreviewError("");
        if (media.link?.endsWith(" ")) handleEnterPostLink();
    }, [media.link, handleEnterPostLink]);

    return (
        <div className="input-wrapper flex flex-col relative CreatePost">
            <MentionsInput
                value={media.description}
                style={{
                    ...mentionsInputStyle,
                    "&multiLine": {
                        ...mentionsInputStyle["&multiLine"],
                        input: {
                            ...mentionsInputStyle["&multiLine"].input,
                            borderColor: errors.description ? "#d32f2f" : "#887E7E",
                            borderWidth: errors.description ? 1 : 2,
                        },
                    },
                }}
                className="h-32 rounded-md overflow-y-hidden noto-font"
                placeholder="Got something to share? Type it here"
                onSelectCapture={(e) => setDescCursorPos(e.target.selectionStart)}
                onChange={(e) => updateMedia({ description: e.target.value })}
                onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter")
                        tryFetchOG(e.target.value);
                }}
                onBlur={(e) => tryFetchOG(e.target.value)}
            >
                <Mention
                    trigger="@"
                    style={mentionStyle}
                    key={mentionSuggestionsLoading}
                    isLoading={mentionSuggestionsLoading}
                    data={mentionSuggestions?.map((user) => ({
                        id: user.userName,
                        display: user.userName,
                    }))}
                    appendSpaceOnAdd={true}
                    renderSuggestion={(
                        suggestion,
                        search,
                        value,
                        index,
                        focused,
                    ) => (
                        <MyAvatar
                            name={value}
                            src={
                                mentionSuggestions?.find(
                                    (user) => user.userName === suggestion.id,
                                )?.media?.currBucketUrl
                            }
                        />
                    )}
                />
            </MentionsInput>

            <EmojiSelector
                positionClassName="bottom-1 right-1"
                onSelection={handleSelectDescriptionEmoji}
            />
        </div>
    )
}

const AddNewMedia = ({ mediaErrors }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    // still need a description when nothing is uploaded
    // e.g. user may type a link for OpenGraph-type media
    const [descriptionNoMedia, setDescriptionNoMedia] = useState("");

    const [medias, setMediasState] = useState([]);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);
    const inputRef = useRef(null);
    const [isNoMediaUploaded, setIsNoMediaUploaded] = useState(medias.length === 0);
    
    const carouselRef = useRef(null);
    const mediaRefs = useRef([]);
    const [activePage, setActivePage] = useState(0);
    const [showNavigation, setShowNavigation] = useState(false);
    
    const ITEMS_PER_PAGE = 2;
    const totalItemsCount = medias.length + 1; // +1 for the upload box
    const totalPages = Math.ceil(totalItemsCount / ITEMS_PER_PAGE);
    
    // load existing
    const hasInitializedRef = useRef(false);
    const { medias: loadedMedias } = useSelector((state) => state.post);
    useEffect(() => {
        if (loadedMedias && loadedMedias.length > 0 && !hasInitializedRef.current) {
            updateMediasHelper(loadedMedias);
            hasInitializedRef.current = true;
        }
    }, [loadedMedias]);

    useEffect(() => {
        setIsNoMediaUploaded(medias.length === 0);
    }, [medias]);

    // whether to show the navigation
    useEffect(() => {
        const checkOverflow = () => {
            if (carouselRef.current) {
                const { scrollWidth, clientWidth } = carouselRef.current;
                setShowNavigation(scrollWidth > clientWidth);
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [medias]);

    const updateMediasHelper = (updater) => {
        setMediasState((prevMedias) => {
            const newMedias = typeof updater === "function" ? updater(prevMedias) : updater;

            dispatch(setMedias(newMedias));

            if (newMedias.length === 1) {
                dispatch(setPrimaryCarouselItemIndex(0));
            } else if (newMedias.length === 0) {
                dispatch(setPrimaryCarouselItemIndex(-1));
            }

            return newMedias;
        });
    };

    const onFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            let newMedias = [...medias];

            for (const file of e.target.files) {
                const shortenedName = file.name.slice(0, 25) + (file.name.length > 25 ? "..." : "");
                if (file.size > 45 * 1024 * 1024) {
                    toast.error(`File size of "${shortenedName}" should be less than 45MB`);
                    continue;
                }

                newMedias.push({
                    url: URL.createObjectURL(file),
                    type: file.type.split("/")[0],
                    description: "",
                    file: file,
                })
            }

            updateMediasHelper(newMedias);
        }
        if (e.target.value) e.target.value = null;
    };

    const onRemoveMedia = (media) => {
        let newMedias = [...medias];
        newMedias = newMedias.filter((m) => m.url !== media.url);

        const mediaIndex = medias.findIndex((elem) => elem.url === media.url);
        if (activeMediaIndex >= mediaIndex) {
            const newindex = Math.max(0, activeMediaIndex - 1);
            setActiveMediaIndex(newindex);
            dispatch(setPrimaryCarouselItemIndex(newindex));
        }

        updateMediasHelper(newMedias);
    };

    const onMediaClicked = (media) => {
        const index = medias.findIndex(element => element.url === media.url);
        setActiveMediaIndex(index);
        dispatch(setPrimaryCarouselItemIndex(index));
        toast.info(`Attachment ${index + 1} will be the primary preview.`)
    };

    const addOpenGraphMediaHelper = (metadata, link) => {
        const imageUrl = Array.isArray(metadata.ogImage)
            ? metadata.ogImage[0]?.url
            : metadata.ogImage;

        let newMedia = {
            url: imageUrl || link,
            type: "opengraph",
            metadata: metadata,
            link: link,
            hideLinkPreview: false,
            file: null,
            description: descriptionNoMedia || metadata?.ogDescription || "",
        };


        setDescriptionNoMedia("");
        updateMediasHelper((prevMedias) => {
            const alreadyHasURL = (prevMedias.findIndex((media) => media.url === newMedia.url)) != -1;
            if (alreadyHasURL) return prevMedias;
            const updatedMedias = [...prevMedias, newMedia];

            return updatedMedias;
        });
    };

    const scrollToPage = (pageIndex) => {
        const targetItemIndex = pageIndex * ITEMS_PER_PAGE;
        const targetElement = mediaRefs.current[targetItemIndex];

        if (targetElement && carouselRef.current) {
            carouselRef.current.scrollTo({
                left: targetElement.offsetLeft - carouselRef.current.offsetLeft,
                behavior: 'smooth'
            });
            setActivePage(pageIndex);
        }
    };

    const onScroll = () => {
        if (!carouselRef.current) return;
        const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;

        // handle "scrolled to the end" case separately
        if (scrollLeft + clientWidth >= scrollWidth - 5) {
            setActivePage(totalPages - 1);
            return;
        }

        let closestPage = 0;
        let minDistance = Infinity;

        for (let i = 0; i < totalPages; i++) {
            const targetIndex = i * ITEMS_PER_PAGE;
            const el = mediaRefs.current[targetIndex];
            if (el) {
                const distance = Math.abs(el.offsetLeft - carouselRef.current.offsetLeft - scrollLeft);
                if (distance < minDistance) {
                    minDistance = distance;
                    closestPage = i;
                }
            }
        }
        setActivePage(closestPage);
    };

    return (
        <div className="flex flex-col gap-10">
            <div className="w-full">
                <div ref={carouselRef} className="w-full flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar px-2"
                    onScroll={onScroll}>
                    {medias.map((m, index) => (
                        <div
                            key={index}
                            ref={(element) => { mediaRefs.current[index] = element; }}
                            onClick={() => {
                                onMediaClicked(m);
                            }}
                        >
                            <SingleMediaUpload
                                media={m}
                                onRemoveMedia={onRemoveMedia}
                                updateMedia={(newProps) => {
                                    let newMedia = { ...m, ...newProps };
                                    let newMedias = [...medias];
                                    newMedias[index] = newMedia;
                                    updateMediasHelper(newMedias);
                                }}
                                className={clsx(
                                    "aspect-square snap-start w-32 h-32 shrink-0 m-3",
                                    Boolean(mediaErrors[m.url]) ? "border-red-500"
                                        : index === activeMediaIndex ? classes.borderGreen : "",
                                    Boolean(mediaErrors[m.url]) || index === activeMediaIndex ? "border-2" : "",
                                )}
                            />
                        </div>
                    ))}

                    <div
                        ref={(element) => (mediaRefs.current[medias.length] = element)}
                        className={clsx(
                            classes.colorGreen,
                            classes.borderGreen,
                            "flex flex-col items-center justify-center min-h-32 p-4 rounded-md",
                            isNoMediaUploaded ? "w-full" : "aspect-square snap-start w-32 h-32 shrink-0  m-3",
                            "border-dashed border-2 cursor-pointer",
                        )}
                        onClick={() => inputRef.current.click()}
                    >
                        <img
                            src={upload}
                            alt="Upload"
                            className="mb-2 object-contain"
                        />
                        <Typography>{isNoMediaUploaded ? "Add photos or videos" : "Add Media"}</Typography>
                        <Typography fontSize={10} className="text-center">
                            ({isNoMediaUploaded ? "Maximum 45 MB per file" : "Max 45 mbs"})
                        </Typography>
                        <input
                            className="hidden"
                            multiple={true}
                            type="file"
                            accept="image/*,video/*"
                            ref={inputRef}
                            onChange={onFileChange}
                        />
                    </div>
                </div>
                {showNavigation && totalPages > 1 && (
                    <div className="mt-3 flex justify-center gap-2">
                        {Array.from({ length: totalPages }).map((_, pageIndex) => (
                            <button
                                key={pageIndex}
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToPage(pageIndex);
                                }}
                                className={clsx(
                                    "h-2 w-2 rounded-full transition-colors border border-gray-600",
                                    activePage === pageIndex
                                        ? "bg-gray-800"
                                        : "bg-white"
                                )}
                                aria-label={`Go to page ${pageIndex + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
            {medias.length > 0 && <div>
                <Typography className="font-bold mb-3">Selected Media ( {activeMediaIndex + 1} of {medias.length} )</Typography>
                <div className="min-h-32 w-full h-32 overflow-auto">
                    {medias[activeMediaIndex].type === "video" ?
                        <CardMedia
                            crossOrigin="anonymous"
                            component={"video"}
                            controls
                            className={"h-full w-full object-cover md:object-contain"}
                            image={medias[activeMediaIndex].url}
                            alt="Stream Media"
                            muted
                        />
                        : medias[activeMediaIndex].type === "image" ? <img
                            src={medias[activeMediaIndex].url}
                            alt="Upload"
                            className={"h-full w-full object-cover md:object-contain"}
                        />
                            : medias[activeMediaIndex].type === "opengraph" ? <OpenGraphInfo
                                className="w-full max-h-fit"
                                data={medias[activeMediaIndex].metadata} />
                                : null}
                </div>
            </div>}
            <div>
                <Typography className="font-bold mb-2">Description</Typography>
                {medias.length > 0
                    ? <div>
                        <DescriptionInput
                            media={medias[activeMediaIndex]}
                            updateMedia={(newProps) => {
                                updateMediasHelper((prevMedias) => {
                                    let newMedia = { ...prevMedias[activeMediaIndex], ...newProps };
                                    let updatedMedias = [...prevMedias];
                                    updatedMedias[activeMediaIndex] = newMedia;

                                    return updatedMedias;
                                });
                            }}
                            errors={{ description: mediaErrors?.[medias?.[activeMediaIndex]?.url]?.description }}
                            onOpenGraphDetected={addOpenGraphMediaHelper}
                        />
                        {mediaErrors?.[medias?.[activeMediaIndex]?.url]?.description && (
                            <p className="text-xs italic text-red-500 mt-1">
                                {mediaErrors[medias[activeMediaIndex].url].description || "Description is required."}
                            </p>
                        )}
                        {medias[activeMediaIndex].linkPreviewError && (
                            <p className="text-xs italic text-red-500 mt-1">
                                {medias[activeMediaIndex].linkPreviewError}
                            </p>
                        )}
                    </div>
                    : <DescriptionInput
                        media={{ description: descriptionNoMedia, hideLinkPreview: false, link: null }}
                        updateMedia={(newProps) => {
                            setDescriptionNoMedia(newProps.description);
                        }}
                        onOpenGraphDetected={addOpenGraphMediaHelper}
                        errors={{ description: null }}
                    />}
            </div>
        </div>
    )
}

const PostTypeHeader = () => {
    const { userRecord } = useSelector((state) => state.user);

    const {
        isGroupOnlyPost,
        selectedGroups,
    } = useSelector((state) => state.post);

    const [showEventTab, setShowEventTab] = useState(false);
    const [isGroupDisabledForEvents, setIsGroupDisabledForEvents] = useState(false);

    useEffect(() => {
        const checkGroupRole = async () => {
            try {
                if (isGroupOnlyPost && selectedGroups && selectedGroups.length === 1) {
                    const gid = selectedGroups[0];

                    const [groupResp, disabledResp] = await Promise.all([
                        axios.get(`${GET_GROUP}/${gid}/1`),
                        axios.get(`${CHECK_GROUP_DISABLED_FOR_EVENTS}/${gid}`),
                    ]);

                    const grp = groupResp.data.group || groupResp.data;
                    const isAdmin =
                        userRecord?.id === grp?.admin?.id || userRecord?.id === grp?.admin;
                    const isModerator = grp?.moderators?.some(
                        (m) => m?.id === userRecord?.id,
                    );

                    setIsGroupDisabledForEvents(disabledResp.data?.isDisabled === true);
                    setShowEventTab(Boolean(isAdmin || isModerator));
                } else {
                    setShowEventTab(false);
                    setIsGroupDisabledForEvents(false);
                }
            } catch (err) {
                setShowEventTab(false);
                setIsGroupDisabledForEvents(false);
            }
        };
        checkGroupRole();
    }, [isGroupOnlyPost, selectedGroups, userRecord]);

    return <PostTypeTabs
        activeTab="carousel"
        selectedGroups={selectedGroups}
        showEventTab={showEventTab}
        isGroupDisabledForEvents={isGroupDisabledForEvents}
    />
};

const SubmitButton = ({ loading }) => {
    const classes = useStyles();
    const { id } = useParams();

    return (
        <div className="z-10 fixed bottom-0 left-0 w-full flex justify-center border-t">
            <div className="bg-white w-5/6 m-auto md:w-1/2 py-4 md:px-10 flex justify-end">
                <button
                    type="submit"
                    className={clsx(classes.button, "w-24 rounded-3xl p-2 px-6")}
                >
                    {loading ? (
                        <CircularProgress size={"20px"} color="inherit" />
                    ) : (
                        <Typography color={"white"}>
                            {id ? "Update" : "Post"}
                        </Typography>
                    )}
                </button>
            </div>
        </div>
    )
};

const CreateCarouselPost = (res) => {
    useLoggedIn()

    const navigation = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const dispatch = useDispatch();

    const {
        medias,
        primaryCarouselItemIndex,
        connections,
        selectedGroups,
        selectedPostCommunities,
        communities,
        selectedTags,
    } = useSelector((state) => state.post);

    const [loading, setLoading] = useState(false);
    const [mediaErrors, setMediaErrors] = useState([]);
    // store ID if being edited
    const [carouselPostId, setCarouselPostId] = useState(null);

    const onSubmit = async () => {
        if (!medias || medias.length === 0) {
            return toast.error("Please attach a media.");
        }

        const validateOpenGraphMedia = (metadata) => {
            const link = metadata.postLink?.trim();
            if (link && !validator.isURL(link)) {
                return "Please enter a valid link.";
            }

            return null;
        };

        const validateMedia = (media) => {
            const errors = {};
            if (!media.description || String(media.description).trim() === "") {
                errors.description = "Description is required.";
            }
            if (!media.url) {
                errors.url = "Please attach a media.";
            }

            if (media.metadata) {
                const openGraphError = validateOpenGraphMedia(media.metadata);
                if (openGraphError) { errors.metadata = openGraphError; }
            }

            return errors;
        };

        const mediaErrorsCurrent = {};
        let someMediaHasError = false;
        for (let i = 0; i < medias.length; i++) {
            const media = medias[i];
            const errors = validateMedia(media);

            mediaErrorsCurrent[media.url] = errors;

            const thisHasSomeError = Boolean(errors.description || errors.url || errors.metadata);
            someMediaHasError = someMediaHasError || thisHasSomeError;

            if (thisHasSomeError) {
                const nameToShow = `Attachment ${i + 1}`;
                if (errors.description)
                    toast.error(`${nameToShow}: ${errors.description}`);
                if (errors.url)
                    toast.error(`${nameToShow}: ${errors.url}`);
                if (errors.metadata)
                    toast.error(`${nameToShow}: ${errors.metadata}`);
            }
        }
        if (someMediaHasError) {
            setMediaErrors(mediaErrorsCurrent);
            return;
        }
        setMediaErrors([]);

        if (primaryCarouselItemIndex < 0 || primaryCarouselItemIndex > medias.length) {
            return toast.error("Please click on an Attachment to be set as the primary preview.")
        }

        if (!Boolean(selectedPostCommunities?.length)) {
            return toast.error("Please select a community");
        }

        // reorder to place the primary Item at index 0
        const index = primaryCarouselItemIndex;
        const reorderedMedias = [
            medias[index],
            ...medias.filter((_, i) => i !== index)
        ];

        // upload all files to the storage
        try {
            setLoading(true);
            let anyUploadingError = false;
            const uploadedMedias = await Promise.all(reorderedMedias.map(async (media, index) => {
                // no need to uplaod OpenGraph, it's stored as JSON
                if (media.type === "opengraph") {
                    return {
                        description: media.description ?? media.ogDescription,
                        type: media.type,
                        metadata: media.metadata,
                        link: media.link ?? media.requestUrl,
                        hideLinkPreview: media.hideLinkPreview ?? false,
                        // if being edited
                        id: id ? media.id : undefined,
                    };
                }

                // already uploaded (e.g. when editing post)
                if (media.mediaId) {
                    return media;
                }

                const formData = new FormData();
                formData.append("file", media.file);
                const res = await axios.post(UPLOAD_FILE, formData);
                if (res.data) return {
                    ...media,
                    mediaId: res.data,
                };
                else {
                    toast.error(`Something went wrong while uploading media ${index + 1}: ${JSON.stringify(res)}`);
                    anyUploadingError = anyUploadingError || true;
                    return null;
                };
            }));
            setLoading(false);
            if (anyUploadingError) return;

            await axios
                // different route if post is being edited
                .post(id ? `/carousel-post/edit/${id}` : CREATE_CAROUSEL_POST, {
                    // send carousel ID if being edited
                    carouselPostId: id ? carouselPostId : undefined,

                    carouselItems: uploadedMedias,
                    primaryItemIndex: primaryCarouselItemIndex,
                    // other common fields 
                    connections: Array.from(connections),
                    communities: Array.from(communities),
                    groups: Array.from(selectedGroups),
                    selectedPostCommunities: selectedPostCommunities,
                    tags: selectedTags?.map((tag) => tag.id),
                })
                .then((res) => {
                    toast.success("Post created successfully");
                    dispatch(resetPost());
                    dispatch(addToPosts({ ...res.data }));

                    try {
                        const created = res.data || {};
                        const groups = created.groups || [];
                        if (Array.isArray(groups) && groups.length) {
                            groups.forEach((g) => {
                                try {
                                    const gid = typeof g === "object" ? g.id : g;
                                    if (gid) localStorage.removeItem(`group-${gid}-cache`);
                                } catch (err) { }
                            });
                        }
                        try {
                            localStorage.setItem("stream-refresh", Date.now().toString());
                            window.dispatchEvent(new Event("stream:refresh"));
                        } catch (err) { }
                    } catch (err) { }

                    navigation(ROOT, { replace: true });
                });
        } catch (error) {
            setLoading(false);
        }
    };

    // load existing
    useEffect(() => {
        if (id) {
            axios.get(`/post/${id}`).then((res) => {
                const post = res.data;

                const primaryIndex = post.carousel.primaryItemIndex;
                const medias = post.carousel.items.map((item) => {
                    let common = {
                        id: item.id,
                        type: item.type,
                        description: item.description,
                        file: null,
                    }
                    if (item.type !== "opengraph") {
                        common = {
                            ...common,
                            url: item.media.currBucketUrl,
                            mediaId: item.media.id,
                        }
                    } else {
                        const meta =
                            typeof item.metadata === "string"
                                ? (() => {
                                    try {
                                        return JSON.parse(item.metadata);
                                    } catch (e) {
                                        return null;
                                    }
                                })()
                                : item.metadata;
                        common = {
                            ...common,
                            url: item.link,
                            metadata: meta,
                            link: item.link,
                            hideLinkPreview: item.hideLinkPreview,
                        }
                    }
                    return common;
                });

                // firstLinkRef.current = item.link || null;
                // lastFetchedLinkRef.current = item.link || null;

                dispatch(setPrimaryCarouselItemIndex(primaryIndex));
                dispatch(setMedias(medias));
                dispatch(setTags(post.tags ?? []));
                dispatch(addAllToConnections(post.connections?.map((x) => x.id) ?? []));
                dispatch(addAllToGroups(post.groups?.map((x) => x.id) ?? []));
                dispatch(addToPostCommunities(post.selectedPostCommunities?.map(x => x.id) ?? []));
                dispatch(addAllToCommunities(post.communities?.map(x => x.id) ?? []));

                setCarouselPostId(post.carousel.id);
            });
        }

        return () => {
            dispatch(resetPost());
        };
    }, []);

    return (
        <Layout>
            <Title
                onBack={() => {
                    const returnTo = location?.state?.returnTo;
                    if (returnTo) {
                        navigation(returnTo, { replace: true });
                    } else {
                        const gid =
                            (selectedGroups &&
                                selectedGroups.length > 0 &&
                                selectedGroups[0]) ||
                            id;
                        if (gid) navigation(`/group/info/${gid}`, { replace: true });
                        else navigation(ROOT, { replace: true });
                    }
                }}
            >
                Add to Stream
            </Title>
            <PostTypeHeader />
            <div className="m-auto flex w-5/6 flex-col gap-4">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit();
                    }}
                    encType="multipart/form-data"
                    className="flex flex-col gap-2 mb-[100px] pt-4"
                    noValidate
                >
                    <div>
                        <Typography className="font-bold mb-2">Media Uploads</Typography>
                        <AddNewMedia mediaErrors={mediaErrors} />
                        {!id && (
                            <div className="mt-10">
                                <AddComCon open={false} />
                            </div>
                        )}
                    </div>
                    <SubmitButton loading={loading} />
                </form>
            </div>
        </Layout>
    )
}

export default CreateCarouselPost;
